import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Archive, Pencil } from "lucide-react";

const EMPTY = { name: "", description: "", sort_order: 100 };

export default function ExpenseCategories() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    const r = await api.get("/expense-categories", { params: { include_archived: showArchived } });
    setList(r.data);
  };
  useEffect(() => { load(); }, [showArchived]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, description: c.description || "", sort_order: c.sort_order || 100 }); setOpen(true); };

  const submit = async () => {
    try {
      if (editing) await api.patch(`/expense-categories/${editing.id}`, form);
      else await api.post("/expense-categories", form);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleArchive = async (c) => {
    try { await api.patch(`/expense-categories/${c.id}`, { archived: !c.archived }); toast.success(c.archived ? "Unarchived" : "Archived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Expense categories"
        subtitle="Global master list. Each project chooses which categories apply from its project settings."
        actions={
          <div className="flex gap-2 items-center">
            <label className="text-xs flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={showArchived} onChange={(e)=>setShowArchived(e.target.checked)} /> Show archived
            </label>
            <Button data-testid="new-ecat-btn" className="fab-m" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>Add category</Button>
          </div>
        }
      />
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Description</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Order</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {list.map((c) => (
              <tr key={c.id} data-testid={`ecat-row-${c.id}`} className="border-t border-border hover:bg-muted/40">
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{c.description || "-"}</td>
                <td className="px-4 py-3 text-muted-foreground tabular">{c.sort_order}</td>
                <td className="px-4 py-3">{c.archived ? <span className="text-xs px-2 py-1 bg-stone-200 rounded">Archived</span> : <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded">Active</span>}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`edit-ecat-${c.id}`} size="sm" variant="ghost" onClick={()=>openEdit(c)}><Pencil className="h-3.5 w-3.5"/></Button>
                    <Button data-testid={`archive-ecat-${c.id}`} size="sm" variant="outline" onClick={()=>toggleArchive(c)}>
                      <Archive className="h-3.5 w-3.5 mr-1"/>{c.archived ? "Restore" : "Archive"}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">No categories.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setEditing(null); setForm(EMPTY);}}}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Name</Label><Input data-testid="ecat-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})}/></div>
            <div><Label>Description (optional)</Label><Input data-testid="ecat-desc" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})}/></div>
            <div><Label>Sort order</Label><Input data-testid="ecat-order" type="number" value={form.sort_order} onChange={(e)=>setForm({...form,sort_order:Number(e.target.value)})}/></div>
          </div>
          <DialogFooter><Button data-testid="ecat-save" onClick={submit}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
